import React, { useState, useEffect } from "react";
import { Box } from "@primer/react";
import { ToastNotification } from "./ToastNotification";

export type ToastPosition =
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left"
  | "center";

export interface Toast {
  id: string;
  message: string;
  variant?: "success" | "danger" | "warning";
  downloadUrl?: string;
  expiresAt?: string;
  subtitle?: string;
  autoCloseMs?: number;
  icon?: "check" | "alert" | "sync" | "blocked";
  position?: ToastPosition;
}

class ToastManagerClass {
  private listeners: Set<(toasts: Toast[]) => void> = new Set();
  private toasts: Toast[] = [];

  subscribe(listener: (toasts: Toast[]) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach((listener) => listener([...this.toasts]));
  }

  show(toast: Omit<Toast, "id">) {
    const id = `toast-${Date.now()}-${Math.random()}`;
    this.toasts.push({ ...toast, position: toast.position ?? "top-right", id });
    this.notify();
    return id;
  }

  update(id: string, fields: Partial<Omit<Toast, "id">>) {
    const toast = this.toasts.find((t) => t.id === id);
    if (toast) {
      Object.assign(toast, fields);
      this.notify();
    }
  }

  remove(id: string) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.notify();
  }

  getToasts(): Toast[] {
    return [...this.toasts];
  }
}

export const toastManager = new ToastManagerClass();

const POSITION_STYLES: Record<ToastPosition, Record<string, unknown>> = {
  "top-right": { top: 16, right: 16 },
  "top-left": { top: 16, left: 16 },
  "bottom-right": { bottom: 16, right: 16 },
  "bottom-left": { bottom: 16, left: 16 },
  center: { top: 16, left: "50%", transform: "translateX(-50%)" },
};

const SLIDE_TRANSFORMS: Record<ToastPosition, string> = {
  "top-right": "translateX(100%)",
  "top-left": "translateX(-100%)",
  "bottom-right": "translateX(100%)",
  "bottom-left": "translateX(-100%)",
  center: "scale(0.95)",
};

export const ToastManager: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsubscribe = toastManager.subscribe((newToasts) => {
      setToasts(newToasts);
    });

    // Get initial toasts
    setToasts(toastManager.getToasts());

    return unsubscribe;
  }, []);

  if (toasts.length === 0) return null;

  const grouped = new Map<ToastPosition, Toast[]>();
  for (const toast of toasts) {
    const pos = toast.position ?? "top-right";
    const group = grouped.get(pos);
    if (group) {
      group.push(toast);
    } else {
      grouped.set(pos, [toast]);
    }
  }

  return (
    <>
      {Array.from(grouped.entries()).map(([position, groupToasts]) => {
        const isBottom = position.startsWith("bottom");
        const slideFrom = SLIDE_TRANSFORMS[position];

        return (
          <Box
            key={position}
            sx={{
              position: "fixed",
              zIndex: 1000,
              display: "flex",
              flexDirection: isBottom ? "column-reverse" : "column",
              gap: 2,
              pointerEvents: "none",
              "& > *": {
                pointerEvents: "auto",
              },
              ...POSITION_STYLES[position],
            }}
          >
            {groupToasts.map((toast, index) => (
              <Box
                key={toast.id}
                sx={{
                  animation: `slideIn-${position} 0.2s ease-out ${index * 0.05}s both`,
                  [`@keyframes slideIn-${position}`]: {
                    from: {
                      opacity: 0,
                      transform: slideFrom,
                    },
                    to: {
                      opacity: 1,
                      transform: position === "center" ? "scale(1)" : "translateX(0)",
                    },
                  },
                }}
              >
                <ToastNotification
                  message={toast.message}
                  variant={toast.variant}
                  downloadUrl={toast.downloadUrl}
                  expiresAt={toast.expiresAt}
                  subtitle={toast.subtitle}
                  autoCloseMs={toast.autoCloseMs}
                  icon={toast.icon}
                  onClose={() => toastManager.remove(toast.id)}
                />
              </Box>
            ))}
          </Box>
        );
      })}
    </>
  );
};
